import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { useAuth } from "../../App";
import { Button } from "../../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import {
  Sparkles,
  Shield,
  Droplets,
  DollarSign,
  Car,
  Star,
  AlertTriangle,
  RefreshCw,
  ChevronDown,
  ChevronUp,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ICON_MAP = {
  shield: Shield,
  drop: Droplets,
  money: DollarSign,
  car: Car,
  star: Star,
  warning: AlertTriangle,
};

function ReasonLine({ icon, title, detail, tone = "primary" }) {
  const Icon = ICON_MAP[icon] || Sparkles;
  const toneClass = {
    primary: "border-l-primary bg-primary/5 text-primary",
    blue: "border-l-blue-500 bg-blue-500/5 text-blue-600 dark:text-blue-300",
    green: "border-l-green-500 bg-green-500/5 text-green-600 dark:text-green-300",
    purple: "border-l-purple-500 bg-purple-500/5 text-purple-600 dark:text-purple-300",
    red: "border-l-red-500 bg-red-500/5 text-red-600 dark:text-red-300",
  }[tone];
  return (
    <div
      className={`flex items-start gap-2.5 p-2.5 rounded-md border-l-4 ${toneClass}`}
      data-testid="ai-reason-line"
    >
      <Icon className="h-4 w-4 mt-0.5 shrink-0" />
      <div className="text-xs leading-snug">
        <p className="font-semibold text-foreground">{title}</p>
        {detail && <p className="text-muted-foreground mt-0.5">{detail}</p>}
      </div>
    </div>
  );
}

function VerdictBadge({ verdict }) {
  if (!verdict || verdict === "safe") return null;
  const map = {
    ai_pick: { label: "AI PICK", cls: "bg-primary text-primary-foreground" },
    runner_up: { label: "RUNNER-UP", cls: "bg-green-500/15 text-green-600 dark:text-green-300 border border-green-500/40" },
    under_spec: { label: "UNDER-SPEC", cls: "bg-red-500/15 text-red-600 dark:text-red-300 border border-red-500/40" },
    unsafe: { label: "UNSAFE", cls: "bg-red-500 text-white" },
  };
  const m = map[verdict];
  if (!m) return null;
  return (
    <span
      className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-bold tracking-wider ${m.cls}`}
      data-testid={`ai-verdict-${verdict}`}
    >
      {m.label}
    </span>
  );
}

function PickCard({ quote, picked, summary, label, accent = "primary" }) {
  if (!quote) return null;
  const accentBorder = accent === "green" ? "border-green-500/60" : "border-primary";
  const reasons = picked?.primary_reasons || [];

  return (
    <div
      className={`rounded-xl border-2 ${accentBorder} bg-card p-4 space-y-3`}
      data-testid={`ai-${accent === "green" ? "runner-up" : "pick"}-card`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
            {label}
          </p>
          <p className="font-heading text-lg font-bold truncate">
            {quote.shop_name}
          </p>
          <p className="text-xs text-muted-foreground">
            {quote.tyre_brand} {quote.tyre_model}
            {quote.speed_rating && (
              <>
                {" · "}
                <span className="font-semibold text-foreground">
                  {quote.speed_rating}
                </span>
              </>
            )}
            {quote.distance_km != null && ` · ${quote.distance_km.toFixed(1)} km`}
          </p>
        </div>
        <div className="text-right shrink-0">
          <p className="font-heading text-2xl font-bold text-primary leading-none">
            ${Number(quote.total_price || quote.price || 0).toFixed(0)}
          </p>
          <p className="text-[10px] text-muted-foreground mt-1">incl. GST</p>
          <p className="text-[10px] text-muted-foreground">
            score {picked?.score}/100
          </p>
        </div>
      </div>

      {summary && (
        <p className="text-sm leading-snug text-foreground/90">{summary}</p>
      )}

      {reasons.length > 0 && (
        <div className="space-y-1.5">
          {reasons.map((r, i) => (
            <ReasonLine
              key={i}
              icon={r.icon}
              title={r.title}
              detail={r.detail}
              tone={accent === "green" ? "green" : "blue"}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function RankedRow({ entry, quote }) {
  if (!quote) return null;
  const unsafe = entry.verdict === "unsafe" || entry.verdict === "under_spec";
  return (
    <div
      className={`grid grid-cols-[28px_1.2fr_1.3fr_70px_60px_75px] gap-2 items-center py-2 px-2 rounded text-xs ${
        unsafe ? "opacity-70 bg-red-500/5" : "hover:bg-muted/40"
      }`}
      data-testid={`ai-ranked-row-${entry.quote_id}`}
    >
      <div
        className={`font-bold text-center ${
          entry.rank === 1
            ? "text-primary"
            : entry.rank === 2
            ? "text-green-600 dark:text-green-300"
            : "text-muted-foreground"
        }`}
      >
        {entry.rank}
      </div>
      <div className="min-w-0">
        <p className="font-semibold truncate">{quote.shop_name}</p>
        <p className="text-[10px] text-muted-foreground">
          {quote.google_rating != null && `★ ${quote.google_rating.toFixed(1)}`}
          {quote.google_review_count != null &&
            ` (${quote.google_review_count})`}
          {quote.distance_km != null && ` · ${quote.distance_km.toFixed(1)} km`}
        </p>
      </div>
      <div className="min-w-0">
        <p className="font-medium truncate">
          {quote.tyre_brand} {quote.tyre_model}
        </p>
        <p className="text-[10px] text-muted-foreground">
          {quote.speed_rating || "—"} · {quote.stock_availability?.replace(/_/g, " ") || "in stock"}
        </p>
      </div>
      <div className="text-right font-bold tabular-nums">
        ${Number(quote.total_price || quote.price || 0).toFixed(0)}
      </div>
      <div
        className={`text-right font-bold tabular-nums ${
          entry.rank === 1
            ? "text-primary"
            : entry.rank === 2
            ? "text-green-600 dark:text-green-300"
            : unsafe
            ? "text-red-500"
            : "text-muted-foreground"
        }`}
      >
        {entry.score}
      </div>
      <div className="text-center">
        <VerdictBadge verdict={entry.verdict} />
      </div>
    </div>
  );
}

export function AIRecommendationDialog({ open, onOpenChange, request, quotes }) {
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [staleInfo, setStaleInfo] = useState(null); // { previous, current }
  const [showMethodology, setShowMethodology] = useState(false);

  const requestId = request?.request_id;
  const { token } = useAuth();
  const authConfig = token ? { headers: { Authorization: `Bearer ${token}` } } : {};

  const quotesById = (quotes || []).reduce((acc, q) => {
    acc[q.quote_id] = q;
    return acc;
  }, {});

  const loadCached = useCallback(async () => {
    if (!requestId) return;
    setLoading(true);
    setError(null);
    setData(null);
    setStaleInfo(null);
    try {
      const res = await axios.get(
        `${API}/customer/ai-recommendation/${requestId}`,
        authConfig,
      );
      if (res.data.status === "cached") {
        setData(res.data.recommendation);
      } else {
        setStaleInfo({
          previous: res.data.previous_quote_count,
          current: res.data.quote_count,
        });
      }
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not load recommendation.");
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestId]);

  useEffect(() => {
    if (open && requestId) loadCached();
  }, [open, requestId, loadCached]);

  const handleGenerate = async () => {
    if (!requestId) return;
    setGenerating(true);
    setError(null);
    try {
      const res = await axios.post(
        `${API}/customer/ai-recommendation/${requestId}`,
        {},
        { ...authConfig, timeout: 120000 },
      );
      setData(res.data.recommendation);
      setStaleInfo(null);
    } catch (e) {
      setError(
        e?.response?.data?.detail ||
          "The AI engine couldn't generate a recommendation. Please try again.",
      );
    } finally {
      setGenerating(false);
    }
  };

  // Derived: pick + runner-up
  const picked = data?.ranked_quotes?.find((r) => r.verdict === "ai_pick");
  const runnerUp =
    data?.ranked_quotes?.find((r) => r.verdict === "runner_up") ||
    data?.ranked_quotes?.find((r) => r.rank === 2 && r.quote_id !== picked?.quote_id);

  const pickedQuote = picked ? quotesById[picked.quote_id] : null;
  const runnerUpQuote = runnerUp ? quotesById[runnerUp.quote_id] : null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-[1100px] w-[98vw] h-[92vh] overflow-hidden p-0 flex flex-col"
        data-testid="ai-recommendation-dialog"
      >
        <DialogHeader className="px-5 sm:px-6 py-3 border-b shrink-0">
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 rounded-lg bg-gradient-to-br from-primary to-purple-500 flex items-center justify-center shrink-0">
              <Sparkles className="h-5 w-5 text-white" />
            </div>
            <div className="min-w-0 flex-1">
              <DialogTitle className="font-heading text-lg sm:text-xl leading-tight flex items-center gap-2 flex-wrap">
                <span>AI Recommendation</span>
                <span
                  className="px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-600 dark:text-amber-300 border border-amber-500/40 text-[9px] font-bold tracking-wider"
                  data-testid="ai-dialog-beta-badge"
                >
                  BETA
                </span>
                <span className="text-muted-foreground font-normal text-sm">
                  · {request?.request_number || `#${request?.request_id}`}
                </span>
              </DialogTitle>
              <DialogDescription className="text-xs">
                AI-ranked comparison · checks load/speed rating fit · suggests best
                match for your stated priority
              </DialogDescription>
            </div>
            {data && (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleGenerate}
                disabled={generating}
                className="text-xs"
                data-testid="ai-regenerate-btn"
              >
                <RefreshCw className={`h-3.5 w-3.5 mr-1 ${generating ? "animate-spin" : ""}`} />
                Regenerate
              </Button>
            )}
          </div>
        </DialogHeader>

        <div className="flex-1 min-h-0 overflow-y-auto px-5 sm:px-6 py-4">
          {/* ---------- LOADING / EMPTY STATES ---------- */}
          {loading && (
            <div className="flex items-center justify-center py-16">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          )}

          {!loading && !data && (staleInfo || error || generating) && (
            <div className="text-center py-12 max-w-md mx-auto">
              <div className="h-16 w-16 rounded-2xl bg-gradient-to-br from-primary to-purple-500 mx-auto flex items-center justify-center mb-4">
                <Sparkles className="h-8 w-8 text-white" />
              </div>
              {error && !generating && (
                <p className="text-sm text-red-500 mb-3" data-testid="ai-error">
                  {error}
                </p>
              )}
              {staleInfo && staleInfo.previous != null && !generating && (
                <p className="text-xs text-amber-600 dark:text-amber-400 mb-3">
                  New quotes received since the last recommendation
                  {" "}({staleInfo.previous} → {staleInfo.current}). Regenerate
                  to include them.
                </p>
              )}
              <h2 className="font-heading text-xl font-bold mb-2">
                {generating ? "Analysing your quotes…" : "Compare with AI"}
              </h2>
              <p className="text-sm text-muted-foreground mb-5">
                {generating
                  ? "Checking each tyre against your vehicle's load and speed requirements, weighing safety, brand tier and value. Usually takes 10–15 seconds."
                  : "Get a transparent ranking of all your quotes. The AI checks load/speed rating fit, flags under-spec tyres, and explains its top pick."}
              </p>
              {!generating && (
                <p className="text-[11px] text-amber-600 dark:text-amber-400 mb-4">
                  Beta feature — always verify safety-critical specs (load index, speed rating) with your shop before fitting.
                </p>
              )}
              <Button
                onClick={handleGenerate}
                disabled={generating || (quotes?.length || 0) < 2}
                className="bg-primary text-primary-foreground"
                data-testid="ai-generate-btn"
              >
                <Sparkles className="h-4 w-4 mr-2" />
                {generating
                  ? "Generating…"
                  : staleInfo?.previous != null
                  ? "Regenerate with new quotes"
                  : "Get AI Recommendation"}
              </Button>
              {(quotes?.length || 0) < 2 && (
                <p className="text-[11px] text-muted-foreground mt-3">
                  Need at least 2 quotes to compare.
                </p>
              )}
            </div>
          )}

          {!loading && !data && !staleInfo && !error && !generating && (
            <div className="text-center py-12 max-w-md mx-auto">
              <div className="h-16 w-16 rounded-2xl bg-gradient-to-br from-primary to-purple-500 mx-auto flex items-center justify-center mb-4">
                <Sparkles className="h-8 w-8 text-white" />
              </div>
              <h2 className="font-heading text-xl font-bold mb-2">
                Compare with AI
              </h2>
              <p className="text-sm text-muted-foreground mb-5">
                Get a transparent ranking of all your quotes. The AI checks
                load/speed rating fit, flags under-spec tyres, and explains its
                top pick.
              </p>
              <p className="text-[11px] text-amber-600 dark:text-amber-400 mb-4">
                Beta feature — always verify safety-critical specs (load index, speed rating) with your shop before fitting.
              </p>
              <Button
                onClick={handleGenerate}
                disabled={(quotes?.length || 0) < 2}
                className="bg-primary text-primary-foreground"
                data-testid="ai-generate-btn"
              >
                <Sparkles className="h-4 w-4 mr-2" />
                Get AI Recommendation
              </Button>
              {(quotes?.length || 0) < 2 && (
                <p className="text-[11px] text-muted-foreground mt-3">
                  Need at least 2 quotes to compare.
                </p>
              )}
            </div>
          )}

          {/* ---------- RESULTS ---------- */}
          {!loading && data && (
            <div className="space-y-4">
              {/* Safety alert */}
              {data.safety_alert && (
                <div
                  className="flex items-start gap-3 rounded-xl border-2 border-red-500/40 bg-red-500/5 p-3"
                  data-testid="ai-safety-alert"
                >
                  <AlertTriangle className="h-5 w-5 text-red-500 mt-0.5 shrink-0" />
                  <div className="text-xs leading-relaxed">
                    <p className="font-bold text-red-600 dark:text-red-300 mb-1">
                      Safety check
                    </p>
                    <p className="text-foreground/90">{data.safety_alert}</p>
                  </div>
                </div>
              )}

              {/* Vehicle requirement card */}
              {data.vehicle_requirements && (
                <div
                  className="rounded-lg bg-muted/40 border p-3 text-xs"
                  data-testid="ai-vehicle-req"
                >
                  <p className="font-semibold mb-1 flex items-center gap-1.5">
                    <Shield className="h-3.5 w-3.5 text-primary" />
                    Vehicle requirement
                  </p>
                  <p className="text-muted-foreground leading-relaxed">
                    Min load index{" "}
                    <span className="font-semibold text-foreground">
                      {data.vehicle_requirements.min_load_index ?? "—"}
                    </span>
                    {" · "}min speed rating{" "}
                    <span className="font-semibold text-foreground">
                      {data.vehicle_requirements.min_speed_rating ?? "—"}
                    </span>
                    {data.vehicle_requirements.premium_marque && (
                      <>
                        {" · "}prefer{" "}
                        <span className="font-semibold text-foreground">
                          {data.vehicle_requirements.preferred_homologation}
                        </span>{" "}
                        homologation
                      </>
                    )}
                    {". "}
                    {data.vehicle_requirements.rationale}
                  </p>
                </div>
              )}

              {/* AI Pick */}
              <PickCard
                label="AI Recommended Pick"
                quote={pickedQuote}
                picked={picked}
                summary={data.ai_pick_summary}
                accent="primary"
              />

              {/* Runner-up */}
              {runnerUpQuote && runnerUp && (
                <PickCard
                  label="Runner-up"
                  quote={runnerUpQuote}
                  picked={runnerUp}
                  summary={data.runner_up_summary}
                  accent="green"
                />
              )}

              {/* Full ranked list */}
              {data.ranked_quotes && data.ranked_quotes.length > 0 && (
                <div className="rounded-lg border bg-card p-3">
                  <p className="text-sm font-semibold mb-2">
                    All {data.ranked_quotes.length} quotes ranked
                  </p>
                  <div className="grid grid-cols-[28px_1.2fr_1.3fr_70px_60px_75px] gap-2 text-[10px] uppercase tracking-wider text-muted-foreground font-semibold py-1 px-2 border-b">
                    <div className="text-center">#</div>
                    <div>Shop</div>
                    <div>Tyre</div>
                    <div className="text-right">Total</div>
                    <div className="text-right">Score</div>
                    <div className="text-center">Verdict</div>
                  </div>
                  <div className="divide-y">
                    {data.ranked_quotes.map((entry) => (
                      <RankedRow
                        key={entry.quote_id}
                        entry={entry}
                        quote={quotesById[entry.quote_id]}
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* Methodology — collapsible */}
              {data.methodology && (
                <details
                  className="rounded-lg border bg-muted/30 px-3"
                  open={showMethodology}
                  onToggle={(e) => setShowMethodology(e.target.open)}
                  data-testid="ai-methodology"
                >
                  <summary className="cursor-pointer py-2.5 text-xs font-semibold flex items-center justify-between list-none">
                    <span className="flex items-center gap-1.5">
                      <Sparkles className="h-3.5 w-3.5 text-primary" />
                      Why the AI ranked it this way
                    </span>
                    {showMethodology ? (
                      <ChevronUp className="h-3.5 w-3.5" />
                    ) : (
                      <ChevronDown className="h-3.5 w-3.5" />
                    )}
                  </summary>
                  <p className="pb-3 pt-1 text-xs leading-relaxed text-muted-foreground">
                    {data.methodology}
                  </p>
                </details>
              )}

              <p className="text-[10px] text-muted-foreground text-center pt-1">
                Generated by {data.model?.split("/")?.[1] || "AI"} ·{" "}
                {data.generated_at
                  ? new Date(data.generated_at).toLocaleString()
                  : ""}
                {" "}· This is an AI estimate. Always verify the tyre's load
                rating with your shop before fitting.
              </p>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default AIRecommendationDialog;
